import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Play, Pause, Trash2, Plus, Zap, Mail, Phone, Clock, CheckSquare,
  ArrowRight, Activity, MessageSquare, AlertTriangle, Sparkles, Eye,
  Wand2, ChevronRight, X, Loader2, GitBranch, BarChart3, FileCheck, ClipboardCheck, Save
} from 'lucide-react';
import { toast } from 'sonner';
import { workflowsAPI, leadSettingsAPI } from '../lib/api';
import api from '../lib/api';
import WorkflowFlowBuilder from '../components/WorkflowFlowBuilder';

const NODE_ICONS = {
  email: Mail,
  ai_call: Phone,
  wait: Clock,
  task: CheckSquare,
  update_status: Activity,
  notify: MessageSquare,
  condition: AlertTriangle,
  request_docs: FileCheck,
  check_docs: ClipboardCheck,
};

const NODE_COLORS = {
  email: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
  ai_call: 'bg-green-500/10 text-green-600 border-green-500/30',
  wait: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
  task: 'bg-purple-500/10 text-purple-600 border-purple-500/30',
  update_status: 'bg-cyan-500/10 text-cyan-600 border-cyan-500/30',
  notify: 'bg-pink-500/10 text-pink-600 border-pink-500/30',
  condition: 'bg-orange-500/10 text-orange-600 border-orange-500/30',
  request_docs: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
  check_docs: 'bg-teal-500/10 text-teal-600 border-teal-500/30',
};

const TRIGGER_LABELS = {
  stage_change: 'Stage Change',
  new_lead: 'New Lead',
  score_change: 'Score Change',
  email_event: 'Email Event',
  call_completed: 'Call Completed',
  time_based: 'Time Based',
  manual: 'Manual',
};

// ─── Workflow Visual Node ────────────────────────────────
function WorkflowNode({ node, isLast }) {
  const Icon = NODE_ICONS[node.type] || Zap;
  const colorClass = NODE_COLORS[node.type] || 'bg-muted text-foreground border-border';

  return (
    <div className="flex items-center gap-2" data-testid={`workflow-node-${node.id}`}>
      <div className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border ${colorClass} shadow-sm`}>
        <Icon className="h-4 w-4 shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{node.label || node.type}</p>
          {node.type === 'wait' && (
            <p className="text-[11px] opacity-70">{node.config?.hours || 24}h delay</p>
          )}
        </div>
      </div>
      {!isLast && <ArrowRight className="h-4 w-4 text-muted-foreground/50 shrink-0" />}
    </div>
  );
}

// ─── Workflow Visualizer ─────────────────────────────────
function WorkflowVisualizer({ trigger, nodes }) {
  const triggerLabel = trigger?.type ? (TRIGGER_LABELS[trigger.type] || trigger.type) : 'Trigger';
  const triggerDetail = trigger?.config?.to_stage || trigger?.config?.source || trigger?.config?.event || '';

  return (
    <div className="flex flex-wrap items-center gap-2 py-3 px-1 overflow-x-auto" data-testid="workflow-visualizer">
      {/* Trigger */}
      <div className="flex items-center gap-2.5 px-3 py-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-emerald-600">
        <Zap className="h-4 w-4 shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-medium">{triggerLabel}</p>
          {triggerDetail && <p className="text-xs opacity-70">{triggerDetail}</p>}
        </div>
      </div>
      <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
      {/* Nodes */}
      {(nodes || []).map((node, i) => (
        <WorkflowNode key={node.id || i} node={node} isLast={i === nodes.length - 1} />
      ))}
    </div>
  );
}

// ─── Create Workflow Dialog ──────────────────────────────
function CreateWorkflowDialog({ open, onClose, onCreated, pipelineStages }) {
  const [form, setForm] = useState({
    name: '', description: '',
    triggerType: 'stage_change', triggerConfig: { to_stage: '' },
    nodes: [{ id: 'n1', type: 'email', label: 'Send Email', config: { subject: '', body_prompt: '', auto_send: true }, next_node: null }],
  });
  const [loading, setLoading] = useState(false);

  const addNode = () => {
    const newId = `n${form.nodes.length + 1}`;
    const updated = form.nodes.map((n, i) =>
      i === form.nodes.length - 1 ? { ...n, next_node: newId } : n
    );
    setForm({ ...form, nodes: [...updated, { id: newId, type: 'email', label: '', config: {}, next_node: null }] });
  };

  const removeNode = (idx) => {
    if (form.nodes.length <= 1) return;
    const updated = form.nodes.filter((_, i) => i !== idx);
    const relinked = updated.map((n, i) => ({
      ...n,
      next_node: i < updated.length - 1 ? updated[i + 1].id : null,
    }));
    setForm({ ...form, nodes: relinked });
  };

  const updateNode = (idx, key, value) => {
    const updated = [...form.nodes];
    if (key === 'type') {
      updated[idx] = { ...updated[idx], type: value, label: value.replace('_', ' '), config: {} };
    } else if (key.startsWith('config.')) {
      const configKey = key.slice(7);
      updated[idx] = { ...updated[idx], config: { ...updated[idx].config, [configKey]: value } };
    } else {
      updated[idx] = { ...updated[idx], [key]: value };
    }
    setForm({ ...form, nodes: updated });
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setLoading(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        trigger: { type: form.triggerType, config: form.triggerConfig },
        nodes: form.nodes.map(n => ({ ...n, label: n.label || n.type.replace('_', ' ') })),
        status: 'draft',
      };
      const res = await workflowsAPI.create(payload);
      toast.success('Workflow created!');
      onCreated(res.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create workflow');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitBranch className="h-5 w-5 text-primary" />
            Create Workflow
          </DialogTitle>
          <DialogDescription>Define a trigger and action steps for your automation.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Workflow Name *</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Negotiation Follow-up" className="mt-1.5" data-testid="workflow-name-input" />
            </div>
            <div>
              <Label>Trigger</Label>
              <Select value={form.triggerType} onValueChange={v => setForm({ ...form, triggerType: v, triggerConfig: {} })}>
                <SelectTrigger className="mt-1.5" data-testid="workflow-trigger-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TRIGGER_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {form.triggerType === 'stage_change' && (
            <div>
              <Label>Pipeline Stage</Label>
              <Select
                value={form.triggerConfig.to_stage || ''}
                onValueChange={v => setForm({ ...form, triggerConfig: { ...form.triggerConfig, to_stage: v } })}
              >
                <SelectTrigger className="mt-1.5" data-testid="workflow-stage-select">
                  <SelectValue placeholder="Select a pipeline stage..." />
                </SelectTrigger>
                <SelectContent>
                  {pipelineStages.map(stage => (
                    <SelectItem key={stage.id || stage.name} value={stage.name}>
                      <span className="flex items-center gap-2">
                        {stage.color && <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: stage.color }} />}
                        {stage.name}
                      </span>
                    </SelectItem>
                  ))}
                  {pipelineStages.length === 0 && (
                    <SelectItem value="_none" disabled>No stages configured</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          )}
          {form.triggerType === 'score_change' && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Direction</Label>
                <Select value={form.triggerConfig.direction || 'above'} onValueChange={v => setForm({ ...form, triggerConfig: { ...form.triggerConfig, direction: v } })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="above">Above</SelectItem><SelectItem value="below">Below</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Threshold</Label><Input type="number" value={form.triggerConfig.threshold || 70} onChange={e => setForm({ ...form, triggerConfig: { ...form.triggerConfig, threshold: parseInt(e.target.value) } })} className="mt-1.5" /></div>
            </div>
          )}
          <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="What does this workflow do?" rows={2} className="mt-1.5" /></div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold">Steps</Label>
              <Button size="sm" variant="outline" onClick={addNode} data-testid="add-step-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Add Step</Button>
            </div>
            {form.nodes.map((node, idx) => (
              <div key={node.id} className="border rounded-xl p-4 space-y-3 bg-muted/20 hover:bg-muted/30 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold">{idx + 1}</span>
                    <span className="text-xs font-medium text-muted-foreground">Step {idx + 1}</span>
                  </div>
                  {form.nodes.length > 1 && <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-600 hover:bg-red-500/10" onClick={() => removeNode(idx)}><Trash2 className="h-3.5 w-3.5" /></Button>}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Action Type</Label>
                    <Select value={node.type} onValueChange={v => updateNode(idx, 'type', v)}>
                      <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="email"><span className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-blue-500" /> Send Email</span></SelectItem>
                        <SelectItem value="ai_call"><span className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-green-500" /> AI Call</span></SelectItem>
                        <SelectItem value="wait"><span className="flex items-center gap-2"><Clock className="h-3.5 w-3.5 text-amber-500" /> Wait</span></SelectItem>
                        <SelectItem value="task"><span className="flex items-center gap-2"><CheckSquare className="h-3.5 w-3.5 text-purple-500" /> Create Task</span></SelectItem>
                        <SelectItem value="update_status"><span className="flex items-center gap-2"><Activity className="h-3.5 w-3.5 text-cyan-500" /> Update Status</span></SelectItem>
                        <SelectItem value="notify"><span className="flex items-center gap-2"><MessageSquare className="h-3.5 w-3.5 text-pink-500" /> Notify Team</span></SelectItem>
                        <SelectItem value="request_docs"><span className="flex items-center gap-2"><FileCheck className="h-3.5 w-3.5 text-emerald-500" /> Request Documents</span></SelectItem>
                        <SelectItem value="check_docs"><span className="flex items-center gap-2"><ClipboardCheck className="h-3.5 w-3.5 text-teal-500" /> Check Documents</span></SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Label</Label>
                    <Input value={node.label} onChange={e => updateNode(idx, 'label', e.target.value)} placeholder="Step label" className="mt-1 h-9" />
                  </div>
                </div>
                {node.type === 'email' && (
                  <div className="space-y-2">
                    <div><Label className="text-xs">Email Subject</Label><Input value={node.config?.subject || ''} onChange={e => updateNode(idx, 'config.subject', e.target.value)} placeholder="Subject line" className="mt-1 h-9" /></div>
                    <div><Label className="text-xs">AI Prompt (for body)</Label><Textarea value={node.config?.body_prompt || ''} onChange={e => updateNode(idx, 'config.body_prompt', e.target.value)} placeholder="Describe the email content..." rows={2} className="mt-1" /></div>
                  </div>
                )}
                {node.type === 'ai_call' && (
                  <div><Label className="text-xs">Call Type</Label>
                    <Select value={node.config?.call_type || 'followup'} onValueChange={v => updateNode(idx, 'config.call_type', v)}>
                      <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="discovery">Discovery</SelectItem>
                        <SelectItem value="demo_scheduling">Demo Scheduling</SelectItem>
                        <SelectItem value="followup">Follow-up</SelectItem>
                        <SelectItem value="closing">Closing</SelectItem>
                        <SelectItem value="re_engagement">Re-engagement</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {node.type === 'wait' && (
                  <div><Label className="text-xs">Wait (hours)</Label><Input type="number" value={node.config?.hours || 24} onChange={e => updateNode(idx, 'config.hours', parseInt(e.target.value))} className="mt-1 h-9" /></div>
                )}
                {node.type === 'task' && (
                  <div><Label className="text-xs">Task Title</Label><Input value={node.config?.title || ''} onChange={e => updateNode(idx, 'config.title', e.target.value)} placeholder="Task title" className="mt-1 h-9" /></div>
                )}
                {node.type === 'update_status' && (
                  <div>
                    <Label className="text-xs">New Status</Label>
                    <Select value={node.config?.new_status || ''} onValueChange={v => updateNode(idx, 'config.new_status', v)}>
                      <SelectTrigger className="mt-1 h-9"><SelectValue placeholder="Select stage..." /></SelectTrigger>
                      <SelectContent>
                        {pipelineStages.map(stage => (
                          <SelectItem key={stage.id || stage.name} value={stage.name}>{stage.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {node.type === 'notify' && (
                  <div><Label className="text-xs">Message</Label><Input value={node.config?.message || ''} onChange={e => updateNode(idx, 'config.message', e.target.value)} placeholder="Notification message" className="mt-1 h-9" /></div>
                )}
                {node.type === 'request_docs' && (
                  <div className="space-y-2">
                    <div><Label className="text-xs">Industry Template</Label>
                      <Select value={node.config?.industry || ''} onValueChange={v => updateNode(idx, 'config.industry', v)}>
                        <SelectTrigger className="mt-1 h-9"><SelectValue placeholder="Select industry..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Consulting">Consulting</SelectItem>
                          <SelectItem value="Real Estate">Real Estate</SelectItem>
                          <SelectItem value="SaaS & B2B Tech">SaaS & B2B Tech</SelectItem>
                          <SelectItem value="Healthcare">Healthcare</SelectItem>
                          <SelectItem value="Education & EdTech">Education & EdTech</SelectItem>
                          <SelectItem value="Manufacturing & Industrial">Manufacturing & Industrial</SelectItem>
                          <SelectItem value="Automotive">Automotive</SelectItem>
                          <SelectItem value="Banking & Insurance (BFSI)">Banking & Insurance (BFSI)</SelectItem>
                          <SelectItem value="D2C & Retail">D2C & Retail</SelectItem>
                          <SelectItem value="Construction & Interior">Construction & Interior</SelectItem>
                          <SelectItem value="Travel & Hospitality">Travel & Hospitality</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label className="text-xs">Direction</Label>
                      <Select value={node.config?.direction || 'inbound'} onValueChange={v => updateNode(idx, 'config.direction', v)}>
                        <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="inbound">Inbound (Request FROM lead)</SelectItem>
                          <SelectItem value="outbound">Outbound (Send TO lead)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
                {node.type === 'check_docs' && (
                  <p className="text-xs text-muted-foreground mt-1">Checks pending documents for the lead and sends a reminder email listing only missing docs.</p>
                )}
              </div>
            ))}
          </div>
          {/* Preview */}
          <div className="pt-1">
            <Label className="text-xs text-muted-foreground mb-1 block">Preview</Label>
            <div className="border rounded-xl p-3 bg-muted/10">
              <WorkflowVisualizer trigger={{ type: form.triggerType, config: form.triggerConfig }} nodes={form.nodes} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading} data-testid="create-workflow-submit">
            {loading ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Creating...</> : <><Plus className="h-4 w-4 mr-1.5" /> Create Workflow</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Detail Dialog ───────────────────────────────────────
function WorkflowDetailDialog({ workflow, open, onClose, onRefresh, pipelineStages }) {
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editableWf, setEditableWf] = useState(null);

  useEffect(() => {
    if (open && workflow?.id) {
      workflowsAPI.executions(workflow.id).then(r => setExecutions(r.data?.executions || [])).catch(() => {});
      setEditableWf(workflow);
    }
  }, [open, workflow?.id]);

  const handleFlowChange = (updatedWorkflow) => {
    setEditableWf(updatedWorkflow);
  };

  const handleSaveFlow = async () => {
    if (!editableWf) return;
    try {
      await workflowsAPI.update(editableWf.id, {
        nodes: editableWf.nodes,
        trigger: editableWf.trigger,
      });
      toast.success('Workflow updated');
      onRefresh?.();
    } catch (err) {
      toast.error('Failed to save workflow');
    }
  };

  if (!workflow) return null;
  const isNode = workflow.format === 'node' || workflow.nodes;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle>{workflow.name}</DialogTitle>
              <DialogDescription>{workflow.description || 'No description'}</DialogDescription>
            </div>
            {isNode && (
              <Button size="sm" onClick={handleSaveFlow} data-testid="save-flow-btn">
                <Save className="h-4 w-4 mr-1.5" />
                Save
              </Button>
            )}
          </div>
        </DialogHeader>
        <Tabs defaultValue="flow">
          <TabsList className="mb-3">
            {isNode && <TabsTrigger value="flow" data-testid="tab-flow">Visual Builder</TabsTrigger>}
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="executions">Executions ({executions.length})</TabsTrigger>
          </TabsList>
          {isNode && (
            <TabsContent value="flow" className="min-h-[450px] border rounded-lg overflow-hidden">
              <WorkflowFlowBuilder
                workflow={editableWf || workflow}
                onChange={handleFlowChange}
                pipelineStages={pipelineStages}
              />
            </TabsContent>
          )}
          <TabsContent value="overview" className="space-y-4">
            {isNode && workflow.trigger && (
              <div><Label className="text-xs text-muted-foreground">Visual Flow</Label><WorkflowVisualizer trigger={workflow.trigger} nodes={workflow.nodes || []} /></div>
            )}
            {!isNode && (
              <div className="space-y-2">
                <p className="text-sm"><span className="text-muted-foreground">Trigger:</span> {workflow.trigger_event}</p>
                {(workflow.conditions || []).map((c, i) => (
                  <Badge key={i} variant="outline" className="text-xs">{c.field} {c.operator} {c.value}</Badge>
                ))}
                <p className="text-sm font-medium mt-2">Actions:</p>
                {(workflow.actions || []).map((a, i) => (
                  <div key={i} className="text-sm pl-3 border-l-2 border-muted">{a.type}: {a.title || a.message || a.stage || a.field || '-'}</div>
                ))}
              </div>
            )}
            <div className="grid grid-cols-3 gap-3 pt-2">
              <div className="text-center p-2 rounded bg-muted/50"><p className="text-lg font-semibold">{workflow.execution_count || 0}</p><p className="text-xs text-muted-foreground">Executions</p></div>
              <div className="text-center p-2 rounded bg-muted/50"><p className="text-lg font-semibold capitalize">{workflow.status || (workflow.is_active ? 'active' : 'paused')}</p><p className="text-xs text-muted-foreground">Status</p></div>
              <div className="text-center p-2 rounded bg-muted/50"><p className="text-lg font-semibold">{workflow.nodes?.length || workflow.actions?.length || 0}</p><p className="text-xs text-muted-foreground">Steps</p></div>
            </div>
          </TabsContent>
          <TabsContent value="executions">
            {executions.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No executions yet</p>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {executions.map(ex => (
                  <div key={ex.id} className="flex items-center justify-between p-2 border rounded text-sm">
                    <div>
                      <Badge variant={ex.status === 'completed' ? 'default' : ex.status === 'failed' ? 'destructive' : 'secondary'} className="text-xs">{ex.status}</Badge>
                      <span className="ml-2 text-muted-foreground">{ex.entity_id?.slice(0, 8)}...</span>
                    </div>
                    <span className="text-xs text-muted-foreground">{new Date(ex.started_at || ex.executed_at).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

// ─── Suggestions Panel ───────────────────────────────────
function SuggestionsPanel({ suggestions, industry, onAccept, onClose, accepting }) {
  if (!suggestions.length) return null;

  return (
    <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-transparent" data-testid="suggestions-panel">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">AI Suggestions for {industry || 'Your Industry'}</CardTitle>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-7 w-7 p-0" data-testid="close-suggestions-btn">
            <X className="h-4 w-4" />
          </Button>
        </div>
        <CardDescription>Recommended automations based on your industry. Click "Use This" to add as a draft.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 pt-2">
        {suggestions.map((s, idx) => (
          <div key={idx} className="border rounded-lg p-4 bg-background hover:border-primary/40 transition-colors" data-testid={`suggestion-card-${idx}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-sm">{s.name}</h4>
                <p className="text-xs text-muted-foreground mt-0.5">{s.description}</p>
                <div className="flex items-center gap-1.5 mt-2 text-xs text-muted-foreground">
                  <Zap className="h-3 w-3 text-emerald-500" />
                  <span>{s.trigger_summary}</span>
                </div>
                {s.steps_summary?.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1 mt-2">
                    {s.steps_summary.map((step, i) => (
                      <span key={i} className="inline-flex items-center gap-1 text-xs">
                        {i > 0 && <ChevronRight className="h-3 w-3 text-muted-foreground" />}
                        <Badge variant="outline" className="text-xs font-normal">{step}</Badge>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onAccept(s)}
                disabled={accepting}
                className="shrink-0"
                data-testid={`accept-suggestion-${idx}`}
              >
                {accepting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5 mr-1" />}
                Use This
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

// ─── Main Workflows Page ─────────────────────────────────
export default function Workflows() {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedWf, setSelectedWf] = useState(null);
  const [tab, setTab] = useState('all');
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestIndustry, setSuggestIndustry] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [pipelineStages, setPipelineStages] = useState([]);

  const loadWorkflows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await workflowsAPI.list();
      setWorkflows(res.data?.workflows || []);
    } catch (e) {
      toast.error('Failed to load workflows');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPipelineStages = useCallback(async () => {
    try {
      const res = await leadSettingsAPI.getStages();
      const stages = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      setPipelineStages(stages);
    } catch {
      // Non-critical — fallback to empty
    }
  }, []);

  const handleSuggestWorkflows = async () => {
    setSuggesting(true);
    try {
      const res = await workflowsAPI.suggest();
      const data = res.data || {};
      const taskId = data.task_id;
      if (!taskId) {
        // Direct response (backwards compat)
        setSuggestions(data.suggestions || []);
        setSuggestIndustry(data.industry || '');
        if (!data.suggestions?.length) toast.info('No suggestions generated. Try updating your industry in profile settings.');
        setSuggesting(false);
        return;
      }
      // Poll for completion
      toast.info('AI is generating workflow suggestions...');
      let attempts = 0;
      const maxAttempts = 30; // 30 * 3s = 90s max
      const poll = async () => {
        attempts++;
        try {
          const statusRes = await workflowsAPI.suggestStatus(taskId);
          const statusData = statusRes.data || {};
          if (statusData.status === 'processing') {
            if (attempts < maxAttempts) {
              setTimeout(poll, 3000);
            } else {
              toast.error('Suggestion generation timed out. Please try again.');
              setSuggesting(false);
            }
            return;
          }
          // Completed
          setSuggestions(statusData.suggestions || []);
          setSuggestIndustry(statusData.industry || '');
          if (!statusData.suggestions?.length) toast.info('No suggestions generated. Try updating your industry in profile settings.');
          setSuggesting(false);
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Failed to generate suggestions. Try again.');
          setSuggesting(false);
        }
      };
      setTimeout(poll, 3000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate suggestions. Try again.');
      setSuggesting(false);
    }
  };

  const handleAcceptSuggestion = async (suggestion) => {
    setAccepting(true);
    try {
      await workflowsAPI.acceptSuggestion(suggestion);
      toast.success(`Workflow "${suggestion.name}" created as draft!`);
      setSuggestions(prev => prev.filter(s => s.name !== suggestion.name));
      loadWorkflows();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create workflow from suggestion.');
    } finally {
      setAccepting(false);
    }
  };

  useEffect(() => {
    loadWorkflows();
    loadPipelineStages();
  }, [loadWorkflows, loadPipelineStages]);

  const handleToggle = async (wf) => {
    try {
      const isActive = wf.status === 'active' || wf.is_active;
      if (isActive) {
        await workflowsAPI.pause(wf.id);
        toast.success('Workflow paused');
      } else {
        await workflowsAPI.activate(wf.id);
        toast.success('Workflow activated');
      }
      loadWorkflows();
    } catch (e) {
      toast.error('Failed to update workflow');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this workflow?')) return;
    try {
      await workflowsAPI.delete(id);
      toast.success('Workflow deleted');
      loadWorkflows();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const filtered = tab === 'all' ? workflows
    : tab === 'active' ? workflows.filter(w => w.status === 'active' || w.is_active)
    : tab === 'draft' ? workflows.filter(w => w.status === 'draft')
    : workflows.filter(w => w.status === 'paused' || (!w.is_active && w.status !== 'draft'));

  const activeCount = workflows.filter(w => w.status === 'active' || w.is_active).length;
  const draftCount = workflows.filter(w => w.status === 'draft').length;
  const totalRuns = workflows.reduce((s, w) => s + (w.execution_count || 0), 0);

  return (
    <div className="space-y-6 p-1" data-testid="workflows-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2.5">
            <GitBranch className="h-6 w-6 text-primary" />
            Workflows
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Automate your sales pipeline with AI-powered workflows</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleSuggestWorkflows} disabled={suggesting} data-testid="suggest-workflows-btn" className="gap-1.5">
            <Sparkles className="h-4 w-4" /> {suggesting ? 'Generating...' : 'AI Suggest'}
          </Button>
          <Button onClick={() => setShowCreate(true)} data-testid="create-workflow-btn" className="gap-1.5">
            <Plus className="h-4 w-4" /> Create Workflow
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="border-l-4 border-l-primary/60">
          <CardContent className="py-4 px-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{workflows.length}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Total Workflows</p>
              </div>
              <GitBranch className="h-5 w-5 text-muted-foreground/40" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500/60">
          <CardContent className="py-4 px-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-emerald-600">{activeCount}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Active</p>
              </div>
              <Play className="h-5 w-5 text-emerald-500/40" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500/60">
          <CardContent className="py-4 px-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-amber-600">{draftCount}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Draft</p>
              </div>
              <Wand2 className="h-5 w-5 text-amber-500/40" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-cyan-500/60">
          <CardContent className="py-4 px-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{totalRuns}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Total Runs</p>
              </div>
              <BarChart3 className="h-5 w-5 text-cyan-500/40" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* AI Suggestions Panel */}
      {suggestions.length > 0 && (
        <SuggestionsPanel
          suggestions={suggestions}
          industry={suggestIndustry}
          onAccept={handleAcceptSuggestion}
          onClose={() => setSuggestions([])}
          accepting={accepting}
        />
      )}

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="all">All ({workflows.length})</TabsTrigger>
          <TabsTrigger value="active">Active</TabsTrigger>
          <TabsTrigger value="draft">Draft</TabsTrigger>
          <TabsTrigger value="paused">Paused</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Workflow List */}
      {loading ? (
        <div className="flex items-center justify-center py-16 gap-2 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Loading workflows...</span>
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-14 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-muted/50 flex items-center justify-center mb-4">
              <GitBranch className="h-7 w-7 text-muted-foreground/50" />
            </div>
            <p className="font-medium text-foreground">No workflows yet</p>
            <p className="text-sm text-muted-foreground mt-1.5 max-w-sm mx-auto">Create your first workflow or let AI suggest automations for your industry</p>
            <div className="flex items-center gap-2 mt-5 justify-center">
              <Button variant="outline" onClick={handleSuggestWorkflows} disabled={suggesting} className="gap-1.5">
                <Sparkles className="h-4 w-4" /> {suggesting ? 'Generating...' : 'AI Suggest for My Industry'}
              </Button>
              <Button onClick={() => setShowCreate(true)} className="gap-1.5"><Plus className="h-4 w-4" /> Create Manually</Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map(wf => {
            const isActive = wf.status === 'active' || wf.is_active;
            const isNode = wf.format === 'node' || wf.nodes;
            return (
              <Card key={wf.id} className="group hover:border-primary/30 transition-all duration-200" data-testid={`workflow-card-${wf.id}`}>
                <CardContent className="py-5 px-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2.5 mb-1.5">
                        <h3 className="font-semibold text-base truncate">{wf.name}</h3>
                        <Badge
                          variant={isActive ? 'default' : wf.status === 'draft' ? 'secondary' : 'outline'}
                          className={`text-xs capitalize shrink-0 ${isActive ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                        >
                          {wf.status || (isActive ? 'active' : 'paused')}
                        </Badge>
                        {wf.source === 'brain_chat' && (
                          <Badge variant="outline" className="text-xs shrink-0 border-primary/30 text-primary">
                            <Sparkles className="h-3 w-3 mr-1" /> AI Generated
                          </Badge>
                        )}
                        {wf.source === 'ai_suggestion' && (
                          <Badge variant="outline" className="text-xs shrink-0 border-amber-500/30 text-amber-600">
                            <Wand2 className="h-3 w-3 mr-1" /> AI Suggested
                          </Badge>
                        )}
                      </div>
                      {wf.description && <p className="text-sm text-muted-foreground line-clamp-2">{wf.description}</p>}
                      {/* Visual flow */}
                      {isNode && wf.trigger && wf.nodes && (
                        <div className="mt-2">
                          <WorkflowVisualizer trigger={wf.trigger} nodes={wf.nodes} />
                        </div>
                      )}
                      {!isNode && wf.trigger_event && (
                        <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                          <Badge variant="outline" className="text-xs">{wf.trigger_event}</Badge>
                          <ArrowRight className="h-3 w-3" />
                          <span>{(wf.actions || []).length} action(s)</span>
                        </div>
                      )}
                      <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><BarChart3 className="h-3 w-3" /> {wf.execution_count || 0} runs</span>
                        {wf.last_executed_at && <span>Last: {new Date(wf.last_executed_at).toLocaleDateString()}</span>}
                        <span>Created: {new Date(wf.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0 opacity-60 group-hover:opacity-100 transition-opacity">
                      <Button size="sm" variant="ghost" onClick={() => setSelectedWf(wf)} title="View details" data-testid={`view-workflow-${wf.id}`}><Eye className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => handleToggle(wf)} title={isActive ? 'Pause' : 'Activate'} data-testid={`toggle-workflow-${wf.id}`}>
                        {isActive ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 text-emerald-600" />}
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-600 hover:bg-red-500/10" onClick={() => handleDelete(wf.id)} data-testid={`delete-workflow-${wf.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <CreateWorkflowDialog open={showCreate} onClose={() => setShowCreate(false)} onCreated={() => loadWorkflows()} pipelineStages={pipelineStages} />
      <WorkflowDetailDialog workflow={selectedWf} open={!!selectedWf} onClose={() => setSelectedWf(null)} onRefresh={loadWorkflows} pipelineStages={pipelineStages} />
    </div>
  );
}
